import type { MessengerThread } from '@/api/client';

let threadsList: MessengerThread[] = [];
const threadById = new Map<string, MessengerThread>();
const threadByPeerId = new Map<string, MessengerThread>();

const listListeners = new Set<() => void>();
const listenersByThreadId = new Map<string, Set<() => void>>();
const listenersByPeerId = new Map<string, Set<() => void>>();

function emitSet(set: Set<() => void> | undefined) {
  if (!set) return;
  for (const cb of set) cb();
}

function emitThreadId(threadId: string) {
  emitSet(listenersByThreadId.get(threadId));
}

function emitPeerId(peerId: string) {
  emitSet(listenersByPeerId.get(peerId));
}

function emitList() {
  for (const cb of listListeners) cb();
}

function rebuildIndexes(list: MessengerThread[]) {
  threadById.clear();
  threadByPeerId.clear();
  for (const t of list) {
    threadById.set(t.id, t);
    if (t.thread_kind === 'direct' && t.peer_id) {
      threadByPeerId.set(t.peer_id, t);
    }
  }
}

function areThreadsEqual(a: MessengerThread, b: MessengerThread): boolean {
  return (
    a.id === b.id &&
    a.thread_kind === b.thread_kind &&
    a.title === b.title &&
    a.last_message_at === b.last_message_at &&
    a.last_message_preview === b.last_message_preview &&
    a.last_message_sender_id === b.last_message_sender_id &&
    a.last_message_sender_name === b.last_message_sender_name &&
    a.unread_count === b.unread_count &&
    a.peer_id === b.peer_id &&
    a.peer_name === b.peer_name &&
    a.is_saved === b.is_saved &&
    a.my_member_role === b.my_member_role
  );
}

/** Стабильные ссылки на неизменённые треды + точечный emit по id/peer. */
export function setMessengerThreadsSnapshot(next: MessengerThread[]): void {
  const prevById = new Map(threadById);
  const nextList: MessengerThread[] = [];
  const changedThreadIds = new Set<string>();
  const changedPeerIds = new Set<string>();

  for (const incoming of next) {
    const prev = prevById.get(incoming.id);
    const merged = prev && areThreadsEqual(prev, incoming) ? prev : incoming;
    if (merged !== prev) changedThreadIds.add(incoming.id);
    if (merged.thread_kind === 'direct' && merged.peer_id) {
      const prevPeer = prevById.get(incoming.id);
      if (!prevPeer || prevPeer.peer_id !== merged.peer_id || merged !== prev) {
        changedPeerIds.add(merged.peer_id);
      }
    }
    nextList.push(merged);
  }

  const prevIds = new Set(prevById.keys());
  const nextIds = new Set(nextList.map((t) => t.id));
  let listStructureChanged = prevIds.size !== nextIds.size;
  let unreadAggregateChanged = false;
  if (!listStructureChanged) {
    for (let i = 0; i < nextList.length; i += 1) {
      if (threadsList[i]?.id !== nextList[i]?.id) {
        listStructureChanged = true;
        break;
      }
    }
  }

  for (const id of prevIds) {
    if (!nextIds.has(id)) {
      changedThreadIds.add(id);
      listStructureChanged = true;
      const removed = prevById.get(id);
      if (removed && removed.unread_count > 0) unreadAggregateChanged = true;
    }
  }

  if (!unreadAggregateChanged) {
    for (const incoming of next) {
      const prev = prevById.get(incoming.id);
      if (prev && prev.unread_count !== incoming.unread_count) {
        unreadAggregateChanged = true;
        break;
      }
    }
  }

  threadsList = nextList;
  rebuildIndexes(nextList);

  for (const id of changedThreadIds) emitThreadId(id);
  for (const peerId of changedPeerIds) emitPeerId(peerId);
  /** Суммы на табах «Пользователи»/«Группы» подписаны на listListeners, не на row. */
  if (listStructureChanged || unreadAggregateChanged) emitList();
}

export function getMessengerThreadsList(): readonly MessengerThread[] {
  return threadsList;
}

export function getMessengerThreadById(threadId: string): MessengerThread | undefined {
  return threadById.get(threadId);
}

export function getMessengerThreadByPeerId(peerId: string): MessengerThread | undefined {
  return threadByPeerId.get(peerId);
}

export function subscribeMessengerThreadsList(onChange: () => void): () => void {
  listListeners.add(onChange);
  return () => listListeners.delete(onChange);
}

export function subscribeMessengerThreadById(
  threadId: string,
  onChange: () => void
): () => void {
  let set = listenersByThreadId.get(threadId);
  if (!set) {
    set = new Set();
    listenersByThreadId.set(threadId, set);
  }
  set.add(onChange);
  return () => {
    set!.delete(onChange);
    if (set!.size === 0) listenersByThreadId.delete(threadId);
  };
}

export function subscribeMessengerThreadByPeerId(
  peerId: string,
  onChange: () => void
): () => void {
  let set = listenersByPeerId.get(peerId);
  if (!set) {
    set = new Set();
    listenersByPeerId.set(peerId, set);
  }
  set.add(onChange);
  return () => {
    set!.delete(onChange);
    if (set!.size === 0) listenersByPeerId.delete(peerId);
  };
}

export function resetMessengerThreadsStore(): void {
  threadsList = [];
  threadById.clear();
  threadByPeerId.clear();
  listListeners.clear();
  listenersByThreadId.clear();
  listenersByPeerId.clear();
}
